import { authApi } from "@/api/authApi";
import { getAccessToken, logout, setAccessToken } from "@/store/authStore";
import axios from "axios";

export const api = axios.create({
	baseURL: import.meta.env.VITE_BACKEND_API_BASE_URL,
	withCredentials: true, // Important for handling httpOnly cookies
});

let isRefreshing = false;
interface QueueItem {
	resolve: (token: string | null) => void;
	reject: (error: Error) => void;
}
let failedQueue: QueueItem[] = [];

const processQueue = (error: Error | null, token: string | null = null) => {
	for (const promise of failedQueue) {
		if (error) {
			promise.reject(error);
		} else if (token) {
			promise.resolve(token);
		} else {
			promise.reject(new Error("No token provided"));
		}
	}
	failedQueue = [];
};

export const setupAxiosInterceptors = () => {
	api.interceptors.request.use(
		(config) => {
			if (config.url?.includes("refresh-token")) {
				return config;
			}

			const token = getAccessToken();
			const apiKey = import.meta.env.VITE_X_API_KEY;

			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			}

			// Add API key header if available
			if (apiKey) {
				config.headers['x-api-key'] = apiKey;
			}

			return config;
		},
		(error) => {
			return Promise.reject(error);
		},
	);

	api.interceptors.response.use(
		(response) => {
			return response;
		},
		async (error) => {
			const originalRequest = error.config;

			if (
				error.response?.status === 401 &&
				!originalRequest._retry &&
				!originalRequest.url?.includes("refresh-token")
			) {
				if (isRefreshing) {
					try {
						const token = await new Promise((resolve, reject) => {
							failedQueue.push({ resolve, reject });
						});
						originalRequest.headers.Authorization = `Bearer ${token}`;
						return api(originalRequest);
					} catch (err) {
						return Promise.reject(err);
					}
				}

				originalRequest._retry = true;
				isRefreshing = true;

				try {
					const { accessToken } = await authApi.refreshToken();
					const newAccessToken = accessToken;

					if (!newAccessToken) {
						throw new Error("No access token received from refresh");
					}

					setAccessToken(newAccessToken);
					processQueue(null, newAccessToken);
					originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
					return api(originalRequest);
				} catch (error) {
					processQueue(error as Error, null);
					logout();
					return Promise.reject(error);
				} finally {
					isRefreshing = false;
				}
			}

			return Promise.reject(error);
		},
	);
};
